export type ColumnFieldType = {
  label: string;
  value: string;
  isEditable: boolean;
  selected: boolean;
  isStaticColumn: boolean;
};
export type ColumnObjType = {
  sub_fields?: Array<ColumnFieldType>;
} & ColumnFieldType;

export type GroupByDetailsOptionObj = {
  label: string;
  order: number;
  slug: string;
  total: number;
  filter_values: string[];
};

// Per-group listing pagination/loading bookkeeping. Shared across every grouped
// domain slice (work-order, service-request, quotes, user-management) and the
// GroupingWrapper that renders them — declared once here so the shape can't drift.
export type GroupedDataMetaItem = {
  isLoading: boolean;
  isFetched: boolean;
  currentPage: number;
  hasMore: boolean;
  // Latched when a per-group fetch rejects (non-cancel). Drives the inline error
  // + Retry state in GroupingWrapper and is cleared by setGroupedDataMetaLoading
  // and by each slice's clear-data reducer on filter/view change.
  hasError: boolean;
  // Monotonic token identifying the fetch attempt this meta belongs to. Set by
  // setGroupedDataMetaLoading when an attempt starts; the fulfilled/rejected
  // reducers apply a settling request only when its `_attempt` still matches,
  // so a late success (stale filter's rows) or a superseded cancel can't clobber
  // a newer attempt. Undefined until the first fetch / after a clear-data reset.
  attemptId?: number;
};

export type AppliedFilterPayloadType = {
  fl_group: Array<{
    field: string;
    operation: string;
    values: string[];
  }>;
  group_op?: string;
};

export type AppliedFilterType = {
  fl_group: {
    field: { value: string; label: string };
    operation: { value: string; label: string };
    value:
      | { value: string; label: string }[]
      | { value: string; label: string }
      | string
      | string[]
      | null;
  }[];
  group_op: { value: string; label: string } | null;
};

export type ViewListType = {
  view: {
    label: string;
    value: string;
  };
  is_primary?: boolean;
  is_default: boolean;
  is_pinned: boolean;
  is_able_to_pin?: boolean;
  is_deletable?: boolean;
  is_editable?: boolean;
};

export type CalendarFilterObjType = {
  technician?: {
    value: string;
    label: string;
    user_id: string;
    timezone: string;
  } | null;
  calendarMode?: {
    label: string;
    value: string;
  } | null;
  settings?: {
    limitToWorkingHours?: boolean;
  };
};

export type ViewDetailsType = {
  // we will pass this object to beck-end to save new view.
  filters?: {
    applied: AppliedFilterType;
    operators: {
      [key: string]: {
        value: string;
        label: string;
      };
    };
    fields: Array<{
      value: string;
      label: string;
      ops: Array<string>;
      values_type: { [key: string]: string };
    }>;
  };
  grouping?: {
    g_by: { value: string; label: string } | null;
    default: string;
    g_options: Array<{
      value: string;
      label: string;
    }>;
  };
  columns?: Array<ColumnObjType>;
  calendarFilter?: CalendarFilterObjType;
};

export type ListViewType = {
  isViewDetailsFetched: boolean;
  hasListViewChanged: {
    hasFilterChanged: boolean;
    hasGroupingChanged: boolean;
    hasColumnsChanged: boolean;
    hasCalendarSettingsChanged: boolean;
    hasTechnicianChanged: boolean;
    hasCalendarModeChanged: boolean;
  };
  groupByDetails?: Array<GroupByDetailsOptionObj>;
  groupCollapseSettings?: { [key: string]: boolean };
  search: {
    searchInput: string;
    recentSearches: Array<string>;
  };
  sort: {
    sortKey: string;
  };
  viewId: string;
  /** All view configs for this module keyed by viewId */
  viewDetailsMap: Record<string, ViewDetailsType>;
  /** Reference snapshot keyed by viewId — used to detect unsaved changes */
  viewDetailsReferenceMap: Record<string, ViewDetailsType>;
  filtersDetails: {
    filterComponent: AppliedFilterType;
    valuesLookup: {
      [key: string]: Array<{
        label: string;
        value: string;
      }>;
    };
  };
  viewList: {
    isViewListLoading: boolean;
    data: Array<ViewListType>;
  };
  priorities: Array<{ value: string; label: string }>;
  calendarDatePickerSelectedDate?: string;
};

export type InitialStateType = {
  'work-orders': ListViewType;
  quotes: ListViewType;
  users: ListViewType;
  calendar: ListViewType;
  tags: ListViewType;
  roles: ListViewType;
  vendors: ListViewType;
  properties: ListViewType;
  'service-requests': ListViewType;
  'work-queues': ListViewType;
  markets: ListViewType;
  fetchingViewDetails: boolean;
  fetchingGroupByDetails: boolean;
  fetchingValuesLookUp: { [key: string]: boolean };
  isViewUpdating: boolean;
  /** true once fetchAllViews has resolved successfully */
  isViewsPrefetched: boolean;
  /** true while fetchAllViews is in-flight */
  isViewsPrefetching: boolean;
  /** true when fetchAllViews failed (endpoint missing or error) — triggers per-module fallback fetches */
  isViewsPrefetchFailed: boolean;
  selectedMarkets: Array<{ label: string; value: string }>;
};

export type FetchAllViewsModuleData = {
  defaultViewId: string | null;
  viewList: Array<ViewListType>;
  viewDetailsMap: Record<string, ViewDetailsType>;
};

export type FetchAllViewsResponse = Partial<Record<string, FetchAllViewsModuleData>>;

export const listViewObj: ListViewType = {
  isViewDetailsFetched: false,
  viewDetailsMap: {},
  viewDetailsReferenceMap: {},
  hasListViewChanged: {
    hasFilterChanged: false,
    hasGroupingChanged: false,
    hasColumnsChanged: false,
    hasCalendarSettingsChanged: false,
    hasTechnicianChanged: false,
    hasCalendarModeChanged: false,
  },
  groupByDetails: [],
  groupCollapseSettings: {},
  search: {
    searchInput: '',
    recentSearches: [],
  },
  sort: {
    sortKey: '',
  },
  viewId: '',
  filtersDetails: {
    filterComponent: {
      fl_group: [],
      group_op: null,
    },
    valuesLookup: {},
  },
  viewList: {
    isViewListLoading: true,
    data: [],
  },
  calendarDatePickerSelectedDate: '',
  priorities: [],
};

const propertiesListViewObj: ListViewType = {
  ...listViewObj,
  sort: { sortKey: 'last_service_at' },
};

export const listViewInitialState = {
  'work-orders': listViewObj,
  quotes: listViewObj,
  users: listViewObj,
  calendar: listViewObj,
  tags: listViewObj,
  roles: listViewObj,
  vendors: listViewObj,
  'service-requests': listViewObj,
  'work-queues': listViewObj,
  markets: listViewObj,
  properties: propertiesListViewObj,
  fetchingViewDetails: false,
  fetchingGroupByDetails: false,
  fetchingValuesLookUp: {} as { [key: string]: boolean },
  isViewUpdating: false,
  isViewsPrefetched: false,
  isViewsPrefetching: false,
  isViewsPrefetchFailed: false,
  selectedMarkets: [] as Array<{ label: string; value: string }>,
};
